import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import DuckCrest from "@/components/DuckCrest";

export default function WalletPassDialog({ open, onClose, user }) {
  const [pass, setPass] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !user) return;
    (async () => {
      setBusy(true);
      try {
        const { data } = await api.get("/wallet/pass");
        setPass(data);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Pass unavailable");
      } finally {
        setBusy(false);
      }
    })();
  }, [open, user]);

  if (!open) return null;

  const fg = pass?.foregroundColor || "rgb(247, 245, 240)";
  const bg = pass?.backgroundColor || "rgb(26, 22, 20)";
  const label = pass?.labelColor || "rgb(184, 163, 128)";

  const addToWallet = () => {
    toast.info("Real .pkpass requires Apple Developer credentials — see backend /wallet/pass for the signed-bundle stub.", { duration: 5000 });
  };

  const copyMemberNo = async () => {
    if (!user?.member_no) return;
    try {
      await navigator.clipboard.writeText(user.member_no);
      toast.success("Member number copied");
    } catch {
      toast.error("Couldn't copy");
    }
  };

  return (
    <div className="dr-modal" onClick={onClose} data-testid="wallet-dialog">
      <div className="dr-modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="dr-modal-eyebrow">APPLE WALLET PREVIEW</div>
        <h2 className="dr-modal-title">Add to Wallet</h2>
        <div className="dr-modal-rule" />

        {/* iOS Wallet pass-style preview */}
        <div className="wallet-pass" style={{ background: bg, color: fg }} data-testid="wallet-pass">
          <div className="wallet-pass-header" style={{ color: label }}>
            <span>{pass?.headerFields?.[0]?.value || "DUCKFarm®"}</span>
            <span className="wallet-pass-org">{pass?.organizationName || "Duck & Rice"}</span>
          </div>

          <div className="wallet-pass-crest">
            <DuckCrest size={56} color={fg.replace("rgb", "rgba").replace(")", ", 0.92)")} />
          </div>

          <div className="wallet-pass-primary">
            <div className="wallet-pass-label" style={{ color: label }}>
              {pass?.generic?.primaryFields?.[0]?.label || "TIER"}
            </div>
            <div className="wallet-pass-value">
              {pass?.generic?.primaryFields?.[0]?.value || user?.tier}
            </div>
          </div>

          <div className="wallet-pass-row">
            {pass?.generic?.secondaryFields?.map((f, i) => (
              <div key={i} className="wallet-pass-field">
                <div className="wallet-pass-label" style={{ color: label }}>{f.label}</div>
                <div className="wallet-pass-value-sm">{f.value}</div>
              </div>
            ))}
          </div>

          <div className="wallet-pass-row aux">
            {pass?.generic?.auxiliaryFields?.map((f, i) => (
              <div key={i} className="wallet-pass-field">
                <div className="wallet-pass-label" style={{ color: label }}>{f.label}</div>
                <div className="wallet-pass-value-xs">{f.value}</div>
              </div>
            ))}
          </div>

          {/* QR code (faux pixel grid, real impl uses qrcode lib) */}
          <div className="wallet-pass-qr-wrap">
            <div className="wallet-pass-qr" aria-hidden="true">
              {Array.from({ length: 64 }, (_, i) => (
                <span key={i} style={{ opacity: ((i * 7919) % 100) > 45 ? 1 : 0 }} />
              ))}
            </div>
            <div className="wallet-pass-qr-alt" style={{ color: label }}>
              {pass?.serialNumber || user?.member_no}
            </div>
          </div>
        </div>

        <p className="wallet-note">
          A real <code>.pkpass</code> bundle requires Apple Developer credentials
          (Pass Type ID, WWDR cert, signing key). The pass JSON is already
          PassKit-spec compliant — the moment those are dropped into the
          backend env, this becomes a one-tap Wallet add.
        </p>

        <div className="dr-actions">
          <button
            type="button"
            className="dr-btn-ghost"
            onClick={copyMemberNo}
            data-testid="wallet-copy-btn"
          >
            COPY MEMBER NO
          </button>
          <button
            type="button"
            className="dr-btn-primary"
            onClick={addToWallet}
            disabled={busy}
            data-testid="wallet-add-btn"
          >
            {busy ? "LOADING…" : "ADD TO APPLE WALLET"}
          </button>
        </div>
      </div>
    </div>
  );
}
